/* Orders / MAR / Notes tabs for the Clinical Patient Profile. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, FileSignature, Ban, CheckCircle2, Pill, FlaskConical, Camera,
  Stethoscope, Apple, Bike, Activity, Edit3, Lock, Calendar, ClipboardCheck,
  AlertTriangle, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ORDER_TYPE_META = {
  medication: { label: "Medication", icon: Pill, color: "#8B5CF6" },
  lab: { label: "Lab", icon: FlaskConical, color: "#0EA5E9" },
  imaging: { label: "Imaging", icon: Camera, color: "#F59E0B" },
  nursing: { label: "Nursing", icon: Stethoscope, color: "#10B981" },
  dietary: { label: "Dietary", icon: Apple, color: "#EF4444" },
  activity: { label: "Activity", icon: Bike, color: "#3B82F6" },
  vitals: { label: "Vitals", icon: Activity, color: "#0B7E8C" },
};

const STATUS_BADGE = {
  draft: { bg: "#FEF3C7", text: "#92400E", label: "Draft" },
  pending_signature: { bg: "#FEF3C7", text: "#92400E", label: "Pending Signature" },
  active: { bg: "#D1FAE5", text: "#065F46", label: "Active" },
  discontinued: { bg: "#FEE2E2", text: "#991B1B", label: "Discontinued" },
  completed: { bg: "#F1F5F9", text: "#475569", label: "Completed" },
};

const MAR_STATUS_BADGE = {
  scheduled: { bg: "#DBEAFE", text: "#1E40AF", label: "Scheduled" },
  given: { bg: "#D1FAE5", text: "#065F46", label: "Given" },
  late: { bg: "#FEF3C7", text: "#92400E", label: "Late" },
  refused: { bg: "#FEE2E2", text: "#991B1B", label: "Refused" },
  held: { bg: "#FED7AA", text: "#9A3412", label: "Held" },
  missed: { bg: "#FEE2E2", text: "#991B1B", label: "Missed" },
};

const FREQUENCIES = ["OD", "BID", "TID", "QID", "Q6H", "Q8H", "Q12H", "HS", "AM", "PM"];

// =================== ORDERS TAB ===================
export function OrdersTab({ patientId, allergies = [] }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [openDiscontinue, setOpenDiscontinue] = useState(null);
  const [discReason, setDiscReason] = useState("");

  const canOrder = ["admin", "physician", "hah_physician"].includes(user?.role_code);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/clinical-docs/orders", { params: { patient_id: patientId, page_size: 100 } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  async function sign(o) {
    try { await api.post(`/clinical-docs/orders/${o.id}/sign`); toast.success("Order signed and active"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function complete(o) {
    try { await api.post(`/clinical-docs/orders/${o.id}/complete`); toast.success("Order completed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function discontinue() {
    if (!discReason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/clinical-docs/orders/${openDiscontinue.id}/discontinue`, { reason: discReason.trim() });
      toast.success("Order discontinued");
      setOpenDiscontinue(null); setDiscReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-3" data-testid="cl-orders-tab">
      <div className="hah-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[#1F2937]">Orders</h3>
          {canOrder && (
            <Button onClick={() => setOpenNew(true)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cd-new-order">
              <Plus className="w-4 h-4 mr-1.5" /> New Order
            </Button>
          )}
        </div>
        {loading ? <div className="text-sm text-[#6B7280]">Loading…</div> : items.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No orders placed.</div>
        ) : (
          <div className="space-y-2">
            {items.map((o) => {
              const meta = ORDER_TYPE_META[o.order_type] || ORDER_TYPE_META.nursing;
              const Icon = meta.icon;
              const badge = STATUS_BADGE[o.status] || STATUS_BADGE.draft;
              return (
                <div key={o.id} className="p-3 border border-[#E5E7EB] rounded-lg" data-testid={`cd-order-row-${o.id}`}>
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}15`, color: meta.color }}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-[#1F2937]">
                          {o.medication_name || o.test_name || o.description || meta.label}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: badge.bg, color: badge.text }}>
                          {badge.label}
                        </span>
                      </div>
                      <div className="text-xs text-[#6B7280] mt-0.5">
                        {o.order_type === "medication" && (
                          <span>{o.dose} · {o.route} · {o.frequency || "PRN"}{o.duration_days ? ` · ${o.duration_days}d` : ""}</span>
                        )}
                        {o.test_name && <span>{o.clinical_indication}</span>}
                        {o.parameters && Object.keys(o.parameters).length > 0 && (
                          <span className="ml-2 text-[10px] text-[#94A3B8]">{Object.entries(o.parameters).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#94A3B8] mt-0.5">
                        Ordered by {o.ordered_by_name} · {new Date(o.ordered_at).toLocaleString()}
                        {o.signed_at && <> · Signed by {o.signed_by_name} · {new Date(o.signed_at).toLocaleString()}</>}
                        {o.discontinue_reason && <span className="text-[#991B1B]"> · DC reason: {o.discontinue_reason}</span>}
                      </div>
                      {o.notes && <div className="text-xs text-[#6B7280] mt-1 italic">"{o.notes}"</div>}
                      <div className="flex items-center gap-2 mt-2">
                        {canOrder && o.status === "draft" && (
                          <Button size="sm" onClick={() => sign(o)} className="h-7 text-xs bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`cd-sign-${o.id}`}>
                            <FileSignature className="w-3 h-3 mr-1" /> Sign
                          </Button>
                        )}
                        {canOrder && o.status === "active" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => complete(o)} className="h-7 text-xs border-[#10B981] text-[#10B981] hover:bg-[#D1FAE5]/40" data-testid={`cd-complete-${o.id}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setOpenDiscontinue(o)} className="h-7 text-xs border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2]/40" data-testid={`cd-discontinue-${o.id}`}>
                              <Ban className="w-3 h-3 mr-1" /> Discontinue
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <NewOrderDialog open={openNew} onOpenChange={setOpenNew} patientId={patientId}
        allergies={allergies}
        onCreated={() => { setOpenNew(false); load(); }} setCreating={setCreating} creating={creating} />

      <Dialog open={!!openDiscontinue} onOpenChange={(v) => { if (!v) { setOpenDiscontinue(null); setDiscReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Discontinue Order</DialogTitle></DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-sm text-[#6B7280]">Provide a reason for discontinuation. Future MAR entries will be cancelled.</p>
            <Textarea rows={3} value={discReason} onChange={(e) => setDiscReason(e.target.value)} placeholder="e.g. allergic reaction noted, regimen changed…" data-testid="cd-disc-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenDiscontinue(null); setDiscReason(""); }} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={discontinue} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="cd-disc-confirm">Discontinue Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewOrderDialog({ open, onOpenChange, patientId, onCreated, creating, setCreating, allergies = [] }) {
  const empty = {
    order_type: "medication", priority: "routine",
    medication_name: "", dose: "", route: "PO", frequency: "TID", duration_days: 7, prn: false, prn_reason: "",
    test_name: "", test_code: "", clinical_indication: "",
    description: "", parameters: "", notes: "",
    acknowledge_allergy_warning: false,
  };
  const [form, setForm] = useState(empty);
  const [allergyCheck, setAllergyCheck] = useState({ action: "clear", matches: [], max_severity: null });
  const [checkingAllergy, setCheckingAllergy] = useState(false);
  useEffect(() => { if (open) { setForm(empty); setAllergyCheck({ action: "clear", matches: [], max_severity: null }); } /* eslint-disable-next-line */ }, [open]);

  // Live drug-allergy check (debounced) whenever medication_name changes
  useEffect(() => {
    if (form.order_type !== "medication" || !form.medication_name?.trim()) {
      setAllergyCheck({ action: "clear", matches: [], max_severity: null });
      return;
    }
    const handle = setTimeout(async () => {
      setCheckingAllergy(true);
      try {
        const r = await api.post(`/patients/${patientId}/check-medication`, { medication_name: form.medication_name });
        setAllergyCheck(r.data);
      } catch { /* silent */ }
      finally { setCheckingAllergy(false); }
    }, 400);
    return () => clearTimeout(handle);
  }, [patientId, form.medication_name, form.order_type]);

  const blocked = allergyCheck.action === "block";
  const needsAck = allergyCheck.action === "acknowledge_required";

  async function submit() {
    setCreating(true);
    try {
      const payload = { patient_id: patientId, order_type: form.order_type, priority: form.priority, notes: form.notes };
      if (form.order_type === "medication") {
        Object.assign(payload, {
          medication_name: form.medication_name, dose: form.dose, route: form.route,
          frequency: form.prn ? null : form.frequency, prn: form.prn, prn_reason: form.prn_reason,
          duration_days: parseInt(form.duration_days) || 7,
          acknowledge_allergy_warning: form.acknowledge_allergy_warning,
        });
      } else if (["lab", "imaging"].includes(form.order_type)) {
        Object.assign(payload, { test_name: form.test_name, test_code: form.test_code, clinical_indication: form.clinical_indication });
      } else {
        Object.assign(payload, { description: form.description });
      }
      await api.post("/clinical-docs/orders", payload);
      toast.success("Order draft created. Sign it to activate.");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New Order</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <FormField label="Order Type">
            <Select value={form.order_type} onValueChange={(v) => setForm({ ...form, order_type: v })}>
              <SelectTrigger data-testid="cd-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ORDER_TYPE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Priority">
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="routine">Routine</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="stat">STAT</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          {form.order_type === "medication" && <>
            <FormField label="Medication" full>
              <Input value={form.medication_name} onChange={(e) => setForm({ ...form, medication_name: e.target.value, acknowledge_allergy_warning: false })} placeholder="e.g. Amoxicillin 500mg" data-testid="cd-form-med-name" />
              {form.order_type === "medication" && allergies.length > 0 && !form.medication_name && (
                <div className="mt-1.5 text-[11px] text-[#92400E] bg-[#FEF3C7] px-2 py-1 rounded border border-[#FDE68A]" data-testid="cd-form-allergy-hint">
                  ⚠ Patient has {allergies.length} active allergy/allergies: {allergies.slice(0, 5).map((a) => a.allergen).join(", ")}{allergies.length > 5 ? ", …" : ""}
                </div>
              )}
              {form.order_type === "medication" && form.medication_name && allergyCheck.matches.length > 0 && (
                <div
                  className="mt-1.5 p-2 rounded border text-[11px]"
                  style={{
                    background: blocked ? "#FEE2E2" : needsAck ? "#FFEDD5" : "#FEF3C7",
                    color: blocked ? "#991B1B" : needsAck ? "#9A3412" : "#92400E",
                    borderColor: blocked ? "#FECACA" : needsAck ? "#FED7AA" : "#FDE68A",
                  }}
                  data-testid="cd-form-allergy-warning"
                >
                  <div className="font-bold uppercase tracking-wider mb-0.5">
                    {blocked ? "🚫 Order will be blocked" : needsAck ? "⚠ Acknowledgement required" : "⚠ Allergy match"}
                    {checkingAllergy ? " · checking…" : ""}
                  </div>
                  <div>
                    Matches: {allergyCheck.matches.map((m) => `${m.allergen} (${m.severity}${m.reaction ? ` — ${m.reaction}` : ""})`).join("; ")}
                  </div>
                  {needsAck && (
                    <label className="flex items-center gap-2 mt-1.5 cursor-pointer text-[11px] font-semibold">
                      <input
                        type="checkbox"
                        checked={form.acknowledge_allergy_warning}
                        onChange={(e) => setForm({ ...form, acknowledge_allergy_warning: e.target.checked })}
                        className="rounded"
                        data-testid="cd-form-allergy-ack"
                      />
                      I acknowledge the severe allergy and proceed with this order.
                    </label>
                  )}
                </div>
              )}
            </FormField>
            <FormField label="Dose">
              <Input value={form.dose} onChange={(e) => setForm({ ...form, dose: e.target.value })} placeholder="500mg" data-testid="cd-form-dose" />
            </FormField>
            <FormField label="Route">
              <Select value={form.route} onValueChange={(v) => setForm({ ...form, route: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["PO", "IV", "IM", "SC", "Topical", "Inhaled", "SL", "PR"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Frequency">
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })} disabled={form.prn}>
                <SelectTrigger data-testid="cd-form-freq"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label="Duration (days)">
              <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} data-testid="cd-form-days" />
            </FormField>
            <FormField label="PRN" full>
              <label className="flex items-center gap-2 text-sm text-[#1F2937]">
                <input type="checkbox" checked={form.prn} onChange={(e) => setForm({ ...form, prn: e.target.checked })} className="rounded" />
                PRN (administer as needed)
              </label>
              {form.prn && <Input className="mt-2" value={form.prn_reason} onChange={(e) => setForm({ ...form, prn_reason: e.target.value })} placeholder="PRN reason e.g. for pain ≥4/10" />}
            </FormField>
          </>}

          {(form.order_type === "lab" || form.order_type === "imaging") && <>
            <FormField label="Test Name" full>
              <Input value={form.test_name} onChange={(e) => setForm({ ...form, test_name: e.target.value })} placeholder="e.g. CBC, Chest X-ray" data-testid="cd-form-test" />
            </FormField>
            <FormField label="Clinical Indication" full>
              <Input value={form.clinical_indication} onChange={(e) => setForm({ ...form, clinical_indication: e.target.value })} placeholder="e.g. r/o pneumonia" />
            </FormField>
          </>}

          {["nursing", "dietary", "activity", "vitals"].includes(form.order_type) && (
            <FormField label="Description" full>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={form.order_type === "vitals" ? "e.g. BP & HR q4h" : "Order details…"} data-testid="cd-form-description" />
            </FormField>
          )}

          <FormField label="Notes" full>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional clinical notes for nursing" />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={submit} disabled={creating || blocked || (needsAck && !form.acknowledge_allergy_warning)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cd-form-create">
            {creating ? "Creating…" : blocked ? "Blocked by allergy" : "Create Draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================== MAR TAB ===================
export function MarTab({ patientId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAdmin, setOpenAdmin] = useState(null);
  const [form, setForm] = useState({ status: "given", dose_given: "", site: "", notes: "", reason_if_not_given: "" });

  const canAdminister = ["admin", "nurse", "hah_nurse"].includes(user?.role_code);

  const load = useCallback(() => {
    setLoading(true);
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(); end.setDate(end.getDate() + 7); end.setHours(23, 59, 59, 999);
    api.get("/clinical-docs/mar", { params: { patient_id: patientId, start: start.toISOString(), end: end.toISOString() } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const e of items) {
      const day = e.scheduled_at.slice(0, 10);
      if (!m.has(day)) m.set(day, []);
      m.get(day).push(e);
    }
    return [...m.entries()].sort();
  }, [items]);

  async function administer() {
    if (form.status !== "given" && !form.reason_if_not_given.trim()) return toast.error("Reason required when not administered");
    try {
      await api.post(`/clinical-docs/mar/${openAdmin.id}/administer`, form);
      toast.success("Administration recorded");
      setOpenAdmin(null); setForm({ status: "given", dose_given: "", site: "", notes: "", reason_if_not_given: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-3" data-testid="cl-mar-tab">
      <div className="hah-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[#1F2937]"><Calendar className="w-4 h-4 inline mr-1.5 text-[#0B7E8C]" /> Medication Administration Record</h3>
          <span className="text-xs text-[#6B7280]">Next 7 days · auto-generated from active medication orders</span>
        </div>
        {loading ? <div className="text-sm text-[#6B7280]">Loading…</div> : items.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No scheduled doses. Sign a medication order to populate the MAR.</div>
        ) : (
          <div className="space-y-4">
            {grouped.map(([day, entries]) => (
              <div key={day}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#6B7280] mb-2">{new Date(day).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {entries.map((e) => {
                    const b = MAR_STATUS_BADGE[e.status] || MAR_STATUS_BADGE.scheduled;
                    const isOverdue = e.overdue;
                    return (
                      <div key={e.id} className={`p-3 border rounded-lg ${isOverdue ? "border-[#EF4444] bg-[#FEE2E2]/30" : "border-[#E5E7EB]"}`} data-testid={`cd-mar-row-${e.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Pill className="w-3.5 h-3.5 text-[#8B5CF6] shrink-0" />
                            <span className="font-medium text-sm text-[#1F2937] truncate">{e.medication_name}</span>
                          </div>
                          <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider shrink-0" style={{ background: b.bg, color: b.text }}>
                            {b.label}
                          </span>
                        </div>
                        <div className="text-xs text-[#6B7280] mt-0.5">{e.dose} · {e.route} · {new Date(e.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{isOverdue && <span className="text-[#EF4444] font-semibold ml-2">OVERDUE</span>}</div>
                        {e.administered_by_name && (
                          <div className="text-[10px] text-[#94A3B8] mt-0.5">By {e.administered_by_name} · {new Date(e.administered_at).toLocaleString()}</div>
                        )}
                        {e.reason_if_not_given && <div className="text-[10px] text-[#991B1B] mt-0.5">Reason: {e.reason_if_not_given}</div>}
                        {canAdminister && (e.status === "scheduled" || e.status === "late") && (
                          <Button size="sm" onClick={() => setOpenAdmin(e)} className="h-7 text-xs mt-2 bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`cd-mar-administer-${e.id}`}>
                            <ClipboardCheck className="w-3 h-3 mr-1" /> Record
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!openAdmin} onOpenChange={(v) => { if (!v) setOpenAdmin(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Administration</DialogTitle></DialogHeader>
          {openAdmin && <div className="text-sm text-[#6B7280] mb-2">{openAdmin.medication_name} · {openAdmin.dose} · scheduled {new Date(openAdmin.scheduled_at).toLocaleString()}</div>}
          <div className="space-y-3 py-2">
            <FormField label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="cd-mar-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="given">Given</SelectItem>
                  <SelectItem value="refused">Refused</SelectItem>
                  <SelectItem value="held">Held</SelectItem>
                  <SelectItem value="missed">Missed</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            {form.status === "given" && <>
              <FormField label="Dose Given">
                <Input value={form.dose_given} onChange={(e) => setForm({ ...form, dose_given: e.target.value })} placeholder="e.g. 500mg" />
              </FormField>
              <FormField label="Site / Route Notes">
                <Input value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="e.g. Right deltoid" />
              </FormField>
            </>}
            {form.status !== "given" && (
              <FormField label="Reason" full>
                <Textarea rows={2} value={form.reason_if_not_given} onChange={(e) => setForm({ ...form, reason_if_not_given: e.target.value })} placeholder="Required when dose not administered" data-testid="cd-mar-reason" />
              </FormField>
            )}
            <FormField label="Notes" full>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAdmin(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={administer} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cd-mar-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =================== NOTES TAB ===================
const CATEGORY_META = {
  physician: { label: "Physician", color: "#0B7E8C" },
  nursing: { label: "Nursing", color: "#10B981" },
  coordination: { label: "Coordination", color: "#F59E0B" },
};

const CATEGORY_BY_ROLE = {
  physician: "physician", hah_physician: "physician", clinical_director: "physician",
  nurse: "nursing", hah_nurse: "nursing",
  care_coordinator: "coordination", hah_coordinator: "coordination",
  admin: "physician",
};

export function NotesTab({ patientId }) {
  const { user } = useAuth();
  const myCategory = CATEGORY_BY_ROLE[user?.role_code];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [openAddendum, setOpenAddendum] = useState(null);
  const [addText, setAddText] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/clinical-docs/notes", { params: { patient_id: patientId } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  function startNew() {
    setEditing({
      id: null, patient_id: patientId, category: myCategory,
      subjective: "", objective: "", assessment: "", plan: "", narrative: "",
    });
  }
  async function save() {
    try {
      if (editing.id) {
        const upd = { subjective: editing.subjective, objective: editing.objective, assessment: editing.assessment, plan: editing.plan, narrative: editing.narrative };
        await api.put(`/clinical-docs/notes/${editing.id}`, upd);
        toast.success("Note updated");
      } else {
        const payload = { patient_id: patientId, category: editing.category };
        if (editing.category === "physician") {
          Object.assign(payload, { subjective: editing.subjective, objective: editing.objective, assessment: editing.assessment, plan: editing.plan });
        } else {
          payload.narrative = editing.narrative;
        }
        await api.post("/clinical-docs/notes", payload);
        toast.success("Note created");
      }
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function sign(n) {
    try { await api.post(`/clinical-docs/notes/${n.id}/sign`); toast.success("Note signed and locked"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function addAddendum() {
    if (!addText.trim()) return toast.error("Addendum text required");
    try {
      await api.post(`/clinical-docs/notes/${openAddendum.id}/addendum`, { text: addText.trim() });
      toast.success("Addendum added");
      setOpenAddendum(null); setAddText(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-3" data-testid="cl-notes-tab">
      <div className="hah-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[#1F2937]">Progress Notes</h3>
          {myCategory && (
            <Button onClick={startNew} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cd-new-note">
              <Plus className="w-4 h-4 mr-1.5" /> New {CATEGORY_META[myCategory].label} Note
            </Button>
          )}
        </div>
        {loading ? <div className="text-sm text-[#6B7280]">Loading…</div> : items.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No progress notes yet.</div>
        ) : (
          <div className="space-y-3">
            {items.map((n) => {
              const meta = CATEGORY_META[n.category];
              const isOwn = n.author_id === user?.id;
              const canEditOwn = isOwn && !n.is_locked;
              const canAddAdd = (CATEGORY_BY_ROLE[user?.role_code] === n.category) || user?.role_code === "admin";
              return (
                <div key={n.id} className="border border-[#E5E7EB] rounded-lg p-4" data-testid={`cd-note-row-${n.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: `${meta.color}15`, color: meta.color }}>
                          {meta.label}
                        </span>
                        <span className="text-sm font-medium text-[#1F2937]">{n.author_name}</span>
                        {n.is_locked && <span className="inline-flex items-center gap-1 text-[10px] text-[#6B7280]"><Lock className="w-3 h-3" /> Signed</span>}
                      </div>
                      <div className="text-xs text-[#94A3B8] mt-0.5">{new Date(n.created_at).toLocaleString()}{n.signed_at && ` · Signed ${new Date(n.signed_at).toLocaleString()}`}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {canEditOwn && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setEditing(n)} className="h-7 text-xs border-[#E5E7EB]" data-testid={`cd-note-edit-${n.id}`}>
                            <Edit3 className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" onClick={() => sign(n)} className="h-7 text-xs bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`cd-note-sign-${n.id}`}>
                            <FileSignature className="w-3 h-3 mr-1" /> Sign
                          </Button>
                        </>
                      )}
                      {n.is_locked && canAddAdd && (
                        <Button size="sm" variant="outline" onClick={() => setOpenAddendum(n)} className="h-7 text-xs border-[#E5E7EB]" data-testid={`cd-note-addendum-${n.id}`}>
                          + Addendum
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 text-sm space-y-1">
                    {n.category === "physician" ? (
                      <>
                        {n.subjective && <div><strong className="text-[#6B7280]">S:</strong> {n.subjective}</div>}
                        {n.objective && <div><strong className="text-[#6B7280]">O:</strong> {n.objective}</div>}
                        {n.assessment && <div><strong className="text-[#6B7280]">A:</strong> {n.assessment}</div>}
                        {n.plan && <div><strong className="text-[#6B7280]">P:</strong> {n.plan}</div>}
                      </>
                    ) : (
                      n.narrative && <div className="text-[#1F2937] whitespace-pre-wrap">{n.narrative}</div>
                    )}
                  </div>
                  {n.addenda && n.addenda.length > 0 && (
                    <div className="mt-3 pl-3 border-l-2 border-[#E5E7EB] space-y-1.5">
                      {n.addenda.map((a) => (
                        <div key={a.id} className="text-xs">
                          <div className="text-[10px] text-[#94A3B8]">Addendum · {a.author_name} · {new Date(a.at).toLocaleString()}</div>
                          <div className="text-[#1F2937] whitespace-pre-wrap">{a.text}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit / Create dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} {editing && CATEGORY_META[editing.category].label} Note</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              {editing.category === "physician" ? (
                <>
                  <FormField label="Subjective" full><Textarea rows={2} value={editing.subjective || ""} onChange={(e) => setEditing({ ...editing, subjective: e.target.value })} placeholder="Patient's reported symptoms…" data-testid="cd-note-s" /></FormField>
                  <FormField label="Objective" full><Textarea rows={2} value={editing.objective || ""} onChange={(e) => setEditing({ ...editing, objective: e.target.value })} placeholder="Vital signs, exam findings…" data-testid="cd-note-o" /></FormField>
                  <FormField label="Assessment" full><Textarea rows={2} value={editing.assessment || ""} onChange={(e) => setEditing({ ...editing, assessment: e.target.value })} placeholder="Clinical impression…" data-testid="cd-note-a" /></FormField>
                  <FormField label="Plan" full><Textarea rows={2} value={editing.plan || ""} onChange={(e) => setEditing({ ...editing, plan: e.target.value })} placeholder="Next steps, orders, follow-up…" data-testid="cd-note-p" /></FormField>
                </>
              ) : (
                <FormField label="Narrative" full><Textarea rows={6} value={editing.narrative || ""} onChange={(e) => setEditing({ ...editing, narrative: e.target.value })} placeholder="Note content…" data-testid="cd-note-narrative" /></FormField>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={save} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cd-note-save">Save Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Addendum */}
      <Dialog open={!!openAddendum} onOpenChange={(v) => { if (!v) { setOpenAddendum(null); setAddText(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Addendum</DialogTitle></DialogHeader>
          <div className="py-2">
            <Textarea rows={4} value={addText} onChange={(e) => setAddText(e.target.value)} placeholder="Addendum content…" data-testid="cd-addendum-text" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenAddendum(null); setAddText(""); }} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={addAddendum} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cd-addendum-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
